import React, { useState, useCallback, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../../../configs/Api';
import { getHeaders } from '../../../middleware/Api';
import Loader from 'components/Loader/Loader';
import { bindActionCreators, Dispatch } from 'redux';
import { enqueueSnackbarCustomMessage } from 'actions/Snackbar/resetSnackbar';
import { connectWithRouter } from 'helpers/connectWithRouter';
import CorpCardIntegrationForm from './forms/CorpCardIntegration';
import PaperTrlIntegrationForm from './forms/PaperTrlIntegration';
import Select from 'components/Inputs/Select/Select';

const styles = require('./styles.pcss');

enum IntegrationTypes {
  CorpCardSync = 'CorpCardSync',
  PaperTrl = 'clientSSO',
}

type Props = {
  organizationId?: string;
  popupOpen: boolean;
  closePopup: Function;
  enqueueSnackbarCustomMessage: Function;
  orgName: string;
  addIntegration: Function;
  integrationToEdit?: any;
  updateIntegration?: Function;
  existingIntegrations?: any[];
}

function IntegrationFormModal(props: Props) {
  const { integrationToEdit } = props;
  const editMode = !!integrationToEdit;

  const [isLoading, setLoading] = useState(false);
  const [integrationType, setIntegrationType] = useState(IntegrationTypes.CorpCardSync);

  useEffect(() => {
    if (props.popupOpen && !editMode) {
      setIntegrationType(IntegrationTypes.CorpCardSync);
    }
  }, [props.popupOpen, editMode]);

  const existingPaperTrl = (props.existingIntegrations ?? []).find(
    (i) => i.type === IntegrationTypes.PaperTrl,
  );
  const isPaperTrl = editMode
    ? integrationToEdit.type === IntegrationTypes.PaperTrl
    : integrationType === IntegrationTypes.PaperTrl;
  // When adding PaperTrl but one already exists, edit that record instead of creating a duplicate.
  const paperTrlToEdit = !editMode && isPaperTrl ? existingPaperTrl : undefined;

  const onSubmit = useCallback(async function (formData) {
    if (!formData.isValid) {
      props.enqueueSnackbarCustomMessage(formData.message, 'error');
      return;
    }

    const targetIntegration = editMode ? integrationToEdit : paperTrlToEdit;
    const isUpdate = !!targetIntegration;

    setLoading(true);
    try {
      if (isUpdate) {
        const body =
          targetIntegration.type === IntegrationTypes.PaperTrl
            ? {
                organizationId: targetIntegration.organizationId,
                type: targetIntegration.type,
                status: formData.data.status,
                clientOrganizationId: formData.data.clientOrganizationId,
              }
            : {
                organizationId: targetIntegration.organizationId,
                type: targetIntegration.type,
                status: formData.data.status,
                companyIds: formData.data.companyIds,
                ignoreFilePrefix: formData.data.ignoreFilePrefix,
                filePrefix: formData.data.filePrefix,
                bucket: formData.data.bucket,
                provider: formData.data.provider,
                allowMatchingByLast4: formData.data.allowMatchingByLast4,
              };
        const res = await (
          await fetch(API_URL + `integrations/${targetIntegration.id}`, {
            method: 'put',
            headers: getHeaders(),
            body: JSON.stringify(body),
          })
        ).json();
        setLoading(false);
        switch (res.type) {
          case 'data':
            props.updateIntegration?.(res.payload);
            props.closePopup();
            return;
          default:
            throw new Error('Server could not update integration');
        }
      } else {
        const data = {
          organizationId: props.organizationId,
          type: integrationType,
          status: 'enabled',
          ...formData.data,
        };
        const res = await (
          await fetch(API_URL + `integrations`, {
            method: 'post',
            headers: getHeaders(),
            body: JSON.stringify(data),
          })
        ).json();
        setLoading(false);
        switch (res.type) {
          case 'data':
            props.addIntegration(res.payload);
            props.closePopup();
            return;
          default:
            throw new Error('Server could not create integration');
        }
      }
    } catch (e) {
      setLoading(false);
      props.enqueueSnackbarCustomMessage(
        editMode || paperTrlToEdit
          ? 'Server could not update integration.'
          : 'Server could not create integration.',
        'error',
      );
    }
  }, [props.addIntegration, props.updateIntegration, integrationType, editMode, integrationToEdit, paperTrlToEdit]);

  const integrationTypeOptions = [
    { value: IntegrationTypes.CorpCardSync, label: 'Corporate Card' },
    { value: IntegrationTypes.PaperTrl, label: 'PaperTrl' },
  ];
  const integrationLabels = {
    [IntegrationTypes.CorpCardSync]: 'Corporate Card',
    [IntegrationTypes.PaperTrl]: 'PaperTrl',
  };

  const isEditingExisting = editMode || !!paperTrlToEdit;
  const paperTrlRecord = editMode ? integrationToEdit : paperTrlToEdit;

  const initialValues = isPaperTrl
    ? paperTrlRecord
      ? {
          clientOrganizationId: paperTrlRecord.settings?.clientOrganizationId,
          status: paperTrlRecord.status,
        }
      : undefined
    : editMode
      ? {
          companyIds: integrationToEdit.settings?.companyIds,
          ignoreFilePrefix: integrationToEdit.settings?.ignoreFilePrefix,
          filePrefix: integrationToEdit.settings?.filePrefix,
          bucket: integrationToEdit.settings?.bucket,
          provider: integrationToEdit.provider,
          allowMatchingByLast4: integrationToEdit.settings?.allowMatchingByLast4,
          status: integrationToEdit.status,
        }
      : undefined;

  return (
    <Dialog
      fullWidth={true}
      scroll="paper"
      open={props.popupOpen}
      className={styles.dialogBox}
    >
      <div>
        {!editMode && (
          <Select
            className={styles.typeSelect}
            menuItems={integrationTypeOptions}
            value={integrationType}
            onChange={({ target: { value } }) => setIntegrationType(value)}
          />
        )}
        <IconButton
          className={styles.closeIcon}
          color="inherit"
          onClick={() => props.closePopup()}
          aria-label="Close"
          size="large"
        >
          <CloseIcon className={styles.svg} />
        </IconButton>
      </div>
      <div className={styles.title}>
        {isEditingExisting
          ? `Editing ${integrationLabels[editMode ? integrationToEdit.type : integrationType] ?? integrationType} Integration for ${props.orgName}`
          : `Adding ${integrationLabels[integrationType]} Integration for ${props.orgName}`}
      </div>
      {isPaperTrl ? (
        <PaperTrlIntegrationForm
          onSubmit={onSubmit}
          editMode={isEditingExisting}
          initialValues={initialValues}
        />
      ) : integrationType === IntegrationTypes.CorpCardSync || editMode ? (
        <CorpCardIntegrationForm
          onSubmit={onSubmit}
          editMode={editMode}
          initialValues={initialValues}
        />
      ) : null}
      {isLoading && (
        <div className={styles.infoLoader}>
          <Loader visible={true} />
        </div>
      )}
    </Dialog>
  );
}

function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators({ enqueueSnackbarCustomMessage }, dispatch);
}

export default connectWithRouter(null, mapDispatchToProps)(IntegrationFormModal);
